import { Design } from "../../core/models/entities/Design";
import {
  CartItem,
  CartItemDesign,
} from "../../presentation/screens/order-create/models/CartItem";

// Flag REACT_APP_DESIGN_REQUIRE_MUL_ENABLED (28/10, order 20011843, org 2057
// Racket Group): the cart carried four «Sejs Badminton 26-28» designs - two
// with a MUL (354875, 354874) and two WITHOUT an item number (399454,
// 408339: drafts / copies whose NAV item does not exist yet). mapCartOrders
// sends productNo = design.product?.mul, i.e. null, for such a line. The
// API's design lookup (DesignRepository.GetByProductNoAsync) then runs
// «Mul IS NULL» - organisation ignored, deleted rows included - and matched
// an arbitrary old draft, 260667 «Arsenal FC - copy», whose logos were
// derived onto the order (OrderDesignLogoLinesFromDesignEnabled). Only the
// strict price guard stopped the order; without it the customer would have
// been invoiced somebody else's prints. The same can happen to any order
// that carries a design without an item number.
//
// The ONE rule, pure and 0 calls: a design may be put in the cart and
// placed only when design.product.mul is a non-blank string. The existing
// orderability guard (status done + votes settled, flag
// designOrderabilityGuardEnabled) never looks at the MUL, is off in prod
// and sits only at add-to-cart - not in the checkout and not over a cart
// revived from localStorage, which is exactly the 20011843 path. Hence the
// two call sites of this rule: the design list's add-to-cart (single +
// bulk: filter + skip toast) and the checkout's handleConfirm (the last
// line: refuse to call PlaceOrder, name the designs, back to the cart). The
// API gets its own fail-closed twin (OrderDesignRequireProductNoEnabled);
// this is the UX in front of it. Callers gate on the flag; these helpers
// do not, so they can be unit-tested as a table.

// i18n keys (all 10 locale files, pinned by designMul.locales.test.ts).
export const DESIGNS_SKIPPED_NO_MUL_KEY = "designListStyle.skippedNoMul";
export const ORDER_DESIGNS_WITHOUT_MUL_KEY =
  "orderConfirmation.designsWithoutMulNotPlaced";
// The lasting mark on a cart line without a MUL (the strip card's tooltip
// and label, the detail panel's note beside the empty Item No.).
export const CART_DESIGN_NO_MUL_KEY = "orderCreate.designNoMulYet";
// The refusal toast (cart Continue / checkout Place order) is the only
// explanation the user gets and it names the lines to remove, so it stays
// until the user closes it (autoClose false: WCAG 2.2.1 timing - a 10 s
// toast was gone before the user had found the lines). The fixed toastId
// keeps a second Continue / Place order from stacking a copy while it is
// still up - the toast's text is replaced instead, so it always names the
// lines of the LAST attempt - and the callers dismiss it by id once the
// rule passes.
export const DESIGNS_WITHOUT_MUL_TOAST_AUTO_CLOSE = false as const;
export const DESIGNS_WITHOUT_MUL_TOAST_ID = "designs-without-mul";
// The toast names at most this many lines, then "+n" - a big cart must not
// turn the toast into a wall of text.
export const MAX_DESIGN_TITLES_IN_TOAST = 5;

/** True when the design carries a non-blank item number (MUL). */
export function designHasMul(design: Design | null | undefined): boolean {
  const mul = design?.product?.mul;
  return typeof mul === "string" && mul.trim().length > 0;
}

export interface MulSplit {
  withMul: Design[];
  withoutMul: Design[];
}

/**
 * The part of a selection that has a MUL and the part that has not, order
 * kept. A nullish entry (defensive) counts as "without a MUL".
 */
export function splitDesignsByMul(designs: readonly Design[]): MulSplit {
  const withMul: Design[] = [];
  const withoutMul: Design[] = [];
  for (const design of designs ?? []) {
    (designHasMul(design) ? withMul : withoutMul).push(design);
  }
  return { withMul, withoutMul };
}

/**
 * The cart's design lines whose design has no MUL - the lines mapCartOrders
 * would post with productNo null / omitted. Logo lines are never this
 * rule's business (their product number is the logo's SKU). A design line
 * without a design at all (broken persisted cart) is reported, not skipped.
 */
export function cartDesignLinesWithoutMul(
  items: readonly CartItem[] | null | undefined,
): CartItemDesign[] {
  return (items ?? []).filter(isDesignLine).filter(cartLineLacksMul);
}

/** True for a design line whose design has no MUL (a logo line: never). */
export function cartLineLacksMul(line: CartItem | null | undefined): boolean {
  return isDesignLine(line) && !designHasMul(line.design);
}

function isDesignLine(item: CartItem | null | undefined): item is CartItemDesign {
  return item?.type === "Design";
}

/**
 * «"Sejs Badminton 26-28" (#399454), "Sejs Badminton 26-28" (#408339),
 * "Sejs Badminton 26-28 U/N" (#413109)» - the lines the refusal toast
 * names so the user can find them in the cart: ONE entry per line, in cart
 * order, never de-duplicated (20011843's cart had four alike «Sejs» titles,
 * two of them valid - a title alone tells nobody which lines to remove).
 * Each entry = the line's title (else the design's), trimmed and quoted,
 * plus the design id (else the line id) as «#id» - unique, and the number
 * support looks up. A line with nothing to show at all is still counted,
 * as "?". At most MAX_DESIGN_TITLES_IN_TOAST entries, then «, +n». "" for
 * no lines.
 */
export function designLineTitles(lines: readonly CartItemDesign[]): string {
  const entries = (lines ?? []).map(lineLabel);
  const shown = entries.slice(0, MAX_DESIGN_TITLES_IN_TOAST);
  const more = entries.length - shown.length;
  return more > 0 ? `${shown.join(", ")}, +${more}` : shown.join(", ");
}

function lineLabel(line: CartItemDesign): string {
  const title = (line?.title ?? "").trim() || (line?.design?.title ?? "").trim();
  const id = line?.design?.id ?? line?.id;
  const ref = id != null && String(id).trim() ? `#${String(id).trim()}` : "";
  if (title) return ref ? `"${title}" (${ref})` : `"${title}"`;
  return ref || '"?"';
}
